import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Typography,
    Box,
    TablePagination,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { getAllTriggers, deleteTrigger } from '../../api';

const TriggerList = () => {
    const [triggers, setTriggers] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalTriggers, setTotalTriggers] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [triggerToDelete, setTriggerToDelete] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchTriggers(currentPage + 1, rowsPerPage);
    }, [currentPage, rowsPerPage]);

    const fetchTriggers = async (page, limit) => {
        try {
            const response = await getAllTriggers({ page, limit });
            setTriggers(response.data.triggers);
            setTotalTriggers(response.data.totalTriggers);
        } catch (error) {
            console.error('Error fetching triggers:', error);
        }
    };

    const handleCreateTrigger = () => {
        navigate('/create-trigger');
    };

    const handleEditTrigger = (id) => {
        navigate(`/edit-trigger/${id}`);
    };

    const handleDeleteTrigger = async () => {
        try {
            await deleteTrigger(triggerToDelete);
            fetchTriggers(currentPage + 1, rowsPerPage);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting trigger:', error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    return (
        <Box>
            {triggers.length === 0 ? (
                <Box textAlign="center" mt={4}>
                    <Typography variant="h6">No triggers available.</Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateTrigger}
                        startIcon={<AddCircleIcon />}
                        style={{ marginTop: 10 }}
                    >
                        Create New Monitor
                    </Button>
                </Box>
            ) : (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateTrigger}
                        startIcon={<AddCircleIcon />}
                        style={{ marginBottom: 10 }}
                    >
                        Create New Monitor
                    </Button>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Monitor Name</TableCell>
                                    <TableCell>Entity Type</TableCell>
                                    <TableCell>Applies to All Entities</TableCell>
                                    <TableCell>Specific Entities</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {triggers.map((trigger) => (
                                    <TableRow key={trigger._id}>
                                        <TableCell>{trigger.name}</TableCell>
                                        <TableCell>{trigger.entityTypeId.name}</TableCell>
                                        <TableCell>{trigger.appliesToAllEntities ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>
                                            {trigger.specificEntities?.length > 0
                                                ? trigger.specificEntities.join(', ')
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditTrigger(trigger._id)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                color="secondary"
                                                onClick={() => {
                                                    setTriggerToDelete(trigger._id);
                                                    setDeleteDialogOpen(true);
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalTriggers}
                        page={currentPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    <Dialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                    >
                        <DialogTitle>Delete Trigger</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Are you sure you want to delete this trigger? This action cannot be undone.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                            <Button
                                onClick={handleDeleteTrigger}
                                color="secondary"
                                variant="contained"
                            >
                                Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Box>
    );
};

export default TriggerList;
